import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitContactSales } from "../services/contact.service";

const ContactSalesPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [goal, setGoal] = useState("Stop counterfeits / label copying");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    const payload = {
      fullName: (formData.get("fullName") as string)?.trim() || "",
      email: (formData.get("email") as string)?.trim() || "",
      company: (formData.get("company") as string)?.trim() || "",
      role: ((formData.get("role") as string) || "").trim() || undefined,
      country: (formData.get("country") as string)?.trim() || "",
      industry: (formData.get("industry") as string)?.trim() || "",
      productCategory: ((formData.get("productCategory") as string) || "").trim() || undefined,
      volume: ((formData.get("volume") as string) || "").trim() || undefined,
      goal: (formData.get("goal") as string)?.trim() || goal,
      timeline: (formData.get("timeline") as string)?.trim() || "",
      message: (formData.get("message") as string)?.trim() || "",
      website: (formData.get("website") as string) || "", // honeypot
    };

    try {
      await submitContactSales(payload);
      setSubmitted(true);
      e.currentTarget.reset();
      setGoal("Stop counterfeits / label copying");
    } catch (err) {
      console.error("cta_contact_sales_submit_failed", err);
      setError("Something went wrong. Please try again or email sales@pureaum.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ color: "#0A2A4A" }}>
            Pureaum
          </Link>
          <Link to="/" className="text-sm font-semibold hover:underline" style={{ color: "#0A2A4A" }}>
            Back to homepage
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <section className="space-y-4">
          <h1 className="text-3xl font-bold" style={{ color: "#0A2A4A" }}>
            Contact Sales
          </h1>
          <p className="text-sm max-w-3xl" style={{ color: "#2B2B2B" }}>
            Get a tailored demo, pricing guidance, and a pilot plan for your product category.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold" style={{ color: "#0A2A4A" }}>
            What Sales will cover
          </h2>
          <ul className="list-disc list-inside space-y-2 text-sm" style={{ color: "#2B2B2B" }}>
            <li>Demo aligned to your product and packaging flow</li>
            <li>Pilot scope: regions, SKUs, packaging levels, success metrics</li>
            <li>Pricing options (pilot to enterprise)</li>
            <li>Integration & rollout plan (API, dashboard, alerts)</li>
            <li>Security & compliance (data handling, access control, audit trail)</li>
          </ul>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          {submitted ? (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4">
              Thanks - received. We'll reach out to schedule next steps.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Full Name *
                  <input name="fullName" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Work Email *
                  <input
                    name="email"
                    type="email"
                    required
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Company *
                  <input name="company" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Role/Title (optional)
                  <input name="role" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Country/Region *
                  <input name="country" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Industry *
                  <select name="industry" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2">
                    {[
                      "Food and beverage",
                      "Pharma / OTC",
                      "CPG / personal care",
                      "Electronics / accessories",
                      "Other",
                    ].map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                Product category (optional)
                <input name="productCategory" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
              </label>
              <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                Estimated volume (optional)
                <select name="volume" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2">
                  {["< 10k units/month", "10k-100k", "100k-1M", "> 1M"].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                  Primary goal *
                  <select
                    name="goal"
                    required
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {[
                      "Stop counterfeits / label copying",
                      "Traceability",
                      "Consumer engagement + trust",
                      "Distributor/retailer visibility",
                      "Investigation & enforcement evidence",
                    ].map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold" style={{ color: "#0A2A4A" }}>
                Timeline *
                <select name="timeline" required className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2">
                  {["Immediate (0-30 days)", "1-3 months", "3-6 months", "Exploring"].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                </label>
              </div>
              <label className="text-sm font-semibold block" style={{ color: "#0A2A4A" }}>
                Message / Requirements *
                <textarea
                  name="message"
                  required
                  rows={4}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
              <input type="text" name="website" className="hidden" tabIndex={-1} aria-hidden="true" />
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                  style={{ backgroundColor: "#FF7A29", color: "white", opacity: loading ? 0.7 : 1 }}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
                <a
                  href="mailto:sales@pureaum.com"
                  className="px-6 py-3 rounded-lg font-semibold border-2 text-center"
                  style={{ color: "#0A2A4A", borderColor: "#0A2A4A" }}
                >
                  Or email sales
                </a>
              </div>
              <p className="text-sm" style={{ color: "#2B2B2B" }}>
                What happens next: we review your note, schedule a short call, and propose a pilot scope with success metrics.
              </p>
              <div className="text-sm" style={{ color: "#2B2B2B" }}>
                Prefer to schedule?{" "}
                <button
                  type="button"
                  onClick={() => console.log("cta_contact_sales_schedule_click")}
                  className="font-semibold"
                  style={{ color: "#0A2A4A" }}
                >
                  Schedule a 20-min intro
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
    </div>
  );
};

export default ContactSalesPage;
